import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Icon,
  Input,
  Select,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { ReactNode, useMemo, useRef, useState } from 'react';
import { BiBarChartSquare } from 'react-icons/bi';
import { FiEdit2, FiInbox, FiPower, FiSearch, FiSliders } from 'react-icons/fi';
import { UseQueryResult, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';

import { apllyToast } from '../../../../components/Global/Toast2.0';
import {
  BI_CATALOG_KEY,
  filterBiReports,
  groupBiReports,
} from '../../../../hooks/useBi';
import {
  BiGroup,
  BiPermission,
  BiReport,
  biErrorMessage,
  biService,
} from '../../../../services/biService';
import { resolveAppearance, sortGroups } from '../../../../utils/biAppearance';
import { BI_PERMISSIONS_KEY } from './useBiAdminData';

// Vínculo com uma permissão que não existe mais (ver ListPowerBiCatalogService
// na API): ninguém consegue ver o relatório.
const INVALID_PERMISSION = '__permissao_invalida__';

type StatusFilter = '' | 'active' | 'inactive';

interface Props {
  catalog: UseQueryResult<BiReport[]>;
  groups: Record<string, BiGroup | undefined>;
  permissions: BiPermission[];
  onEditReport: (report: BiReport) => void;
  onEditGroup: (name: string) => void;
}

interface PendingRemoval {
  report: BiReport;
  permission: string;
}

function count(total: number, one: string, many: string) {
  return `${total} ${total === 1 ? one : many}`;
}

function Label({ children }: { children: ReactNode }) {
  return (
    <Text fontWeight="medium" color="gray.650" mr="10px" whiteSpace="nowrap">
      {children}
    </Text>
  );
}

export function ReportsTab({
  catalog,
  groups,
  permissions,
  onEditReport,
  onEditGroup,
}: Props) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('');
  const [togglingId, setTogglingId] = useState('');
  const [pending, setPending] = useState<PendingRemoval | null>(null);
  const [removing, setRemoving] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const reports = useMemo(() => catalog.data || [], [catalog.data]);
  const activeCount = reports.filter(report => report.status).length;

  const sections = useMemo(() => {
    const byStatus = status
      ? reports.filter(report => report.status === (status === 'active'))
      : reports;
    return sortGroups(
      groupBiReports(filterBiReports(byStatus, search)),
      groups,
    );
  }, [reports, status, search, groups]);

  const toggleStatus = async (report: BiReport) => {
    setTogglingId(report.id);
    try {
      await biService.updateReport(report.id, { status: !report.status });
      await queryClient.invalidateQueries(BI_CATALOG_KEY);
      apllyToast(
        'success',
        report.status ? 'Relatório desativado.' : 'Relatório ativado.',
      );
    } catch (err) {
      apllyToast(
        'error',
        biErrorMessage(err, 'Não foi possível alterar a situação.'),
      );
    } finally {
      setTogglingId('');
    }
  };

  // Remove a permissão pela identificação mostrada na etiqueta e grava a
  // lista completa de ids que sobrou (mesmo caminho do modal de edição).
  const confirmRemoval = async () => {
    if (!pending) {
      return;
    }

    setRemoving(true);
    try {
      const all = permissions.length
        ? permissions
        : await queryClient.fetchQuery(
            BI_PERMISSIONS_KEY,
            biService.listPermissions,
          );
      const targetIds = all
        .filter(permission => permission.identification === pending.permission)
        .map(permission => permission.id);
      const currentIds = await biService.listReportPermissionIds(
        pending.report.id,
      );
      const nextIds = currentIds.filter(id => !targetIds.includes(id));

      if (nextIds.length === currentIds.length) {
        apllyToast('error', 'Permissão não encontrada neste relatório.');
        return;
      }

      await biService.syncReportPermissions(pending.report.id, nextIds);
      await queryClient.invalidateQueries(BI_CATALOG_KEY);
      apllyToast('success', 'Permissão removida.');
      setPending(null);
    } catch (err) {
      apllyToast(
        'error',
        biErrorMessage(err, 'Não foi possível remover a permissão.'),
      );
    } finally {
      setRemoving(false);
    }
  };

  const isLastPermission =
    !!pending &&
    pending.report.permissions.filter(item => item !== pending.permission)
      .length === 0;

  const renderPermissions = (report: BiReport) => {
    if (report.permissions.length === 0) {
      return (
        <Tooltip
          bg="gray.650"
          label="Sem permissão vinculada: todos os usuários veem este relatório."
        >
          <Tag size="md" colorScheme="gray">
            Todos os usuários
          </Tag>
        </Tooltip>
      );
    }

    return report.permissions.map(permission =>
      permission === INVALID_PERMISSION ? (
        <Tooltip
          key={permission}
          bg="gray.650"
          label="Vinculada a uma permissão que não existe mais: ninguém vê este relatório. Edite o relatório para corrigir."
        >
          <Tag size="md" colorScheme="red">
            Permissão inexistente
          </Tag>
        </Tooltip>
      ) : (
        <Tag key={permission} size="md" colorScheme="orange">
          <TagLabel>{permission}</TagLabel>
          <TagCloseButton
            aria-label={`Remover a permissão ${permission}`}
            title="Remover permissão"
            onClick={() => setPending({ report, permission })}
          />
        </Tag>
      ),
    );
  };

  const renderReport = (report: BiReport) => {
    const appearance = resolveAppearance(
      report,
      groups[report.groupNameSamasc || 'Sem grupo'],
      report.groupNameSamasc,
    );
    const text = report.summary || report.description;

    return (
      <Flex
        key={report.id}
        py="3"
        px="2.5"
        borderTopWidth="1px"
        borderColor="gray.700"
        align={{ base: 'stretch', lg: 'center' }}
        justify="space-between"
        direction={{ base: 'column', lg: 'row' }}
        gridGap="3"
      >
        <Flex align="flex-start" gridGap="3" minW="0" flex="1">
          <Icon
            as={appearance.icon}
            color={`${appearance.color}.400`}
            boxSize="5"
            mt="0.5"
            flexShrink={0}
          />
          <Box minW="0" flex="1">
            <Flex wrap="wrap" align="center" gridColumnGap="10" gridRowGap="1">
              <Flex align="center" minW="0">
                <Label>Relatório:</Label>
                <Text fontWeight="medium" color="gray.50">
                  {report.reportName}
                </Text>
              </Flex>
              <Flex align="center">
                <Label>Situação:</Label>
                {report.status ? (
                  <Tag size="sm" colorScheme="green">
                    Ativo
                  </Tag>
                ) : (
                  <Tag size="sm" colorScheme="red">
                    Inativo
                  </Tag>
                )}
              </Flex>
              {report.owner && (
                <Flex align="center">
                  <Label>Responsável:</Label>
                  <Text>{report.owner.name}</Text>
                </Flex>
              )}
            </Flex>

            {text && (
              <Text fontSize="sm" color="gray.500" mt="1" noOfLines={1}>
                {text}
              </Text>
            )}

            <Flex wrap="wrap" align="center" gridGap="1" mt="2">
              <Label>Permissão:</Label>
              {renderPermissions(report)}
            </Flex>
          </Box>
        </Flex>

        <HStack spacing="2" flexShrink={0} justify="flex-end">
          <Tooltip bg="gray.650" label="Editar">
            <Button
              aria-label={`Editar ${report.reportName}`}
              onClick={() => onEditReport(report)}
              type="button"
              colorScheme="green"
              color="white"
            >
              <FiEdit2 />
            </Button>
          </Tooltip>
          <Tooltip bg="gray.650" label="Abrir BI integrado">
            <Button
              as={Link}
              to={`/bi-management/renderization/${report.id}`}
              aria-label={`Abrir ${report.reportName}`}
              colorScheme="yellow"
              color="white"
            >
              <BiBarChartSquare />
            </Button>
          </Tooltip>
          <Tooltip bg="gray.650" label={report.status ? 'Desativar' : 'Ativar'}>
            <Button
              aria-label={`${report.status ? 'Desativar' : 'Ativar'} ${
                report.reportName
              }`}
              onClick={() => toggleStatus(report)}
              isLoading={togglingId === report.id}
              type="button"
              colorScheme={report.status ? 'red' : 'green'}
              color="white"
            >
              <FiPower />
            </Button>
          </Tooltip>
        </HStack>
      </Flex>
    );
  };

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'stretch', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gridGap="3"
        mb="4"
      >
        <Flex gridGap="2" direction={{ base: 'column', sm: 'row' }}>
          <Box position="relative" w={{ base: '100%', sm: '300px' }}>
            <Icon
              as={FiSearch}
              position="absolute"
              left="3"
              top="50%"
              transform="translateY(-50%)"
              color="gray.400"
              zIndex={1}
            />
            <Input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Buscar relatório ou grupo"
              aria-label="Buscar relatório ou grupo"
              pl="10"
            />
          </Box>
          <Select
            value={status}
            onChange={event => setStatus(event.target.value as StatusFilter)}
            aria-label="Filtrar por situação"
            w={{ base: '100%', sm: '210px' }}
          >
            <option value="">Todas as situações</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </Select>
        </Flex>

        {catalog.data && (
          <Text fontSize="sm" color="gray.500">
            {count(reports.length, 'relatório', 'relatórios')} ·{' '}
            {count(activeCount, 'ativo', 'ativos')} ·{' '}
            {count(reports.length - activeCount, 'inativo', 'inativos')}
          </Text>
        )}
      </Flex>

      {catalog.isLoading && (
        <Flex minH="200px" align="center" justify="center">
          <Spinner size="xl" color="blue.500" />
        </Flex>
      )}

      {catalog.isError && !catalog.data && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription flex="1">
            Não foi possível carregar os relatórios.
          </AlertDescription>
          <Button size="sm" onClick={() => catalog.refetch()}>
            Tentar novamente
          </Button>
        </Alert>
      )}

      {catalog.data && sections.length === 0 && (
        <Center py="10" flexDirection="column">
          <Icon as={FiInbox} fontSize="40" color="gray.500" />
          <Text color="gray.400" mt="3">
            Nenhum relatório encontrado
          </Text>
        </Center>
      )}

      {sections.map(section => {
        const appearance = resolveAppearance(
          null,
          groups[section.group],
          section.group,
        );
        const active = section.reports.filter(report => report.status).length;

        return (
          <Box
            key={section.group}
            bg="gray.800"
            p="10px"
            borderRadius="6px"
            borderLeftWidth="4px"
            borderLeftColor={`${appearance.color}.400`}
            mb="10px"
          >
            <Flex
              justify="space-between"
              align="center"
              gridGap="3"
              wrap="wrap"
              px="2.5"
              pb="2"
            >
              <Flex align="center" gridGap="3">
                <Flex
                  w="9"
                  h="9"
                  flexShrink={0}
                  borderRadius="full"
                  bg={`${appearance.color}.100`}
                  color={`${appearance.color}.600`}
                  align="center"
                  justify="center"
                >
                  <Icon as={appearance.icon} boxSize="5" />
                </Flex>
                <Box>
                  <Text as="h2" fontSize="lg">
                    {section.group}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {count(section.reports.length, 'relatório', 'relatórios')} ·{' '}
                    {count(active, 'ativo', 'ativos')}
                  </Text>
                </Box>
              </Flex>

              <Tooltip
                bg="gray.650"
                label="Cor, ícone e ordem do grupo no catálogo"
              >
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<FiSliders />}
                  onClick={() => onEditGroup(section.group)}
                >
                  Personalizar grupo
                </Button>
              </Tooltip>
            </Flex>

            {section.reports.map(renderReport)}
          </Box>
        );
      })}

      <AlertDialog
        isOpen={!!pending}
        leastDestructiveRef={cancelRef}
        onClose={() => !removing && setPending(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.700">
            <AlertDialogHeader>Remover permissão</AlertDialogHeader>
            <AlertDialogBody>
              <Text>
                Remover a permissão <b>{pending?.permission}</b> do relatório{' '}
                <b>{pending?.report.reportName}</b>?
              </Text>
              {isLastPermission && (
                <Alert status="warning" borderRadius="md" mt="4">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    É a única permissão deste relatório: sem ela, todos os
                    usuários do SAMASC passam a vê-lo.
                  </AlertDescription>
                </Alert>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant="ghost"
                onClick={() => setPending(null)}
                isDisabled={removing}
              >
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={confirmRemoval}
                isLoading={removing}
              >
                Remover
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
